import { GeneratedFile, ModelSelected, ResponseTimeAnalytics, SourceFile, SSEResponse, SSEResponseTypes, TokenAnalytics } from '../models/websockets.types';
import { isSyntheticAutoModel } from '../features/chat/utils/resolve-conversation-model';
import { ConversationState } from '../state/store/chat/use-chat-store';
import { Attachment, FileGenerationStatus, Message, MessageGeneration, MessageRole, SearchSource, Source, Mention } from 'features/chat/types/chat';
import { IamData } from 'features/iam/services/user-service';
import {
  DEFAULT_AGENT_CHAT_TITLE,
  DEFAULT_CONVERSATION_NAME,
  DEFAULT_VOICE_CALL_TITLE,
  NEW_CONNECTION_WHILE_STREAMING,
} from './constants/chat-constants';
import { formatShowMessageTimeForSend } from '../features/chat/utils/message-show-time';
import { clearStreamingStatusFields } from '../features/chat/utils/clear-streaming-status-fields';
import { applyStreamAnswerText } from '../features/chat/utils/stream-blocks';
import { getUserDisplayName as resolveUserDisplayName } from './user-display-name';
import { normalizeSourceFromWire } from '../features/chat/utils/source-citations';

function findStreamingPlacement(
  messages: ConversationState['messages'],
  streamingAssistantMessageId?: string | null,
  assistantMessageId?: string,
  userMessageId?: string
): { msgIndex: number; genIndex: number } | null {
  if (assistantMessageId) {
    for (let mi = 0; mi < messages.length; mi++) {
      const msg = messages[mi];
      if (msg.role !== MessageRole.ASSISTANT) continue;
      const gi = msg.generations.findIndex((g) => g.id === assistantMessageId);
      if (gi !== -1) return { msgIndex: mi, genIndex: gi };
      if (msg.recordId === assistantMessageId && msg.generations.length > 0) {
        return { msgIndex: mi, genIndex: msg.generations.length - 1 };
      }
    }
  }

  const sid = streamingAssistantMessageId;
  if (sid) {
    for (let mi = 0; mi < messages.length; mi++) {
      const msg = messages[mi];
      if (msg.role !== MessageRole.ASSISTANT) continue;
      const gi = msg.generations.findIndex((g) => g.id === sid);
      if (gi !== -1) return { msgIndex: mi, genIndex: gi };
    }
  }

  if (userMessageId) {
    const userMsg = findUserMessageForLinkedId(messages, userMessageId);
    if (userMsg) {
      let lastMatch: { msgIndex: number; genIndex: number } | null = null;
      for (let mi = 0; mi < messages.length; mi++) {
        const msg = messages[mi];
        if (msg.role !== MessageRole.ASSISTANT) continue;
        if (resolveUserMessageRecordIdForAssistant(messages, msg) !== userMsg.recordId) {
          continue;
        }
        const inFlightIdx = findInFlightGenerationInMessage(msg);
        if (inFlightIdx !== null) {
          return { msgIndex: mi, genIndex: inFlightIdx };
        }
        const byParent = msg.generations.findIndex(
          (g) =>
            g.parentUserGenerationId === userMessageId ||
            (assistantMessageId != null && g.id === assistantMessageId)
        );
        if (byParent !== -1) {
          return { msgIndex: mi, genIndex: byParent };
        }
        if (msg.generations.length > 0) {
          lastMatch = { msgIndex: mi, genIndex: msg.generations.length - 1 };
        }
      }
      if (lastMatch) return lastMatch;
    }
  }

  for (let mi = messages.length - 1; mi >= 0; mi--) {
    const msg = messages[mi];
    if (msg.role === MessageRole.ASSISTANT && msg.generations.length > 0) {
      return { msgIndex: mi, genIndex: msg.generations.length - 1 };
    }
  }
  return null;
}

function isUserRoleMessage(role: MessageRole): boolean {
  return String(role) === MessageRole.USER;
}

/**
 * Resolves the user message id for a streaming assistant event (relatedUserMessageIds,
 * parentIds chain, or last user message in the conversation).
 */
export function resolveStreamUserMessageId(
  message: {
    metadata?: { relatedUserMessageIds?: string[] };
    parentIds?: string[];
  },
  conv?: Pick<ConversationState, 'messages'>
): string | undefined {
  const fromMeta = message.metadata?.relatedUserMessageIds?.[0];
  if (fromMeta && (!conv?.messages?.length || findUserMessageForLinkedId(conv.messages, fromMeta))) {
    return fromMeta;
  }

  if (conv?.messages?.length && message.parentIds?.length) {
    for (const parentId of message.parentIds) {
      const parent = conv.messages.find(
        (m) => m.recordId === parentId || m.generations.some((g) => g.id === parentId)
      );
      if (!parent) continue;
      if (isUserRoleMessage(parent.role)) {
        return parent.generations.find((g) => g.id === parentId)?.id ?? parent.recordId;
      }
      const resolved = resolveUserMessageRecordIdForAssistant(conv.messages, parent);
      if (resolved) return resolved;
    }
  }

  if (conv?.messages?.length) {
    for (let i = conv.messages.length - 1; i >= 0; i--) {
      const msg = conv.messages[i];
      if (!isUserRoleMessage(msg.role)) continue;
      return msg.generations[msg.generations.length - 1]?.id ?? msg.recordId;
    }
  }

  return fromMeta;
}

function findUserMessageIndex(
  messages: ConversationState['messages'],
  userMessageId: string
): number {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (!isUserRoleMessage(msg.role)) continue;
    if (msg.recordId === userMessageId) return i;
    if (msg.generations.some((g) => g.id === userMessageId)) return i;
  }
  return -1;
}

function assistantMatchesUserTurn(
  msg: Message,
  userMessageId: string,
  messages: ConversationState['messages']
): boolean {
  if (msg.role !== MessageRole.ASSISTANT) return false;
  if (msg.userRecordId === userMessageId) return true;
  if (msg.generations.some((g) => g.parentUserGenerationId === userMessageId)) return true;
  const userMsg = findUserMessageForLinkedId(messages, userMessageId);
  if (!userMsg) return false;
  return resolveUserMessageRecordIdForAssistant(messages, msg) === userMsg.recordId;
}

function findInFlightGenerationInMessage(
  msg: Message
): number | null {
  for (let gi = msg.generations.length - 1; gi >= 0; gi--) {
    const gen = msg.generations[gi];
    if (!gen) continue;
    if (gen.isThisGenerationOnGoing) {
      return gi;
    }
    // Live tool/skill/subagent flags only (not streamStatusEntries — those persist after END).
    if (
      gen.toolCallStreamStatus != null ||
      gen.skillStreamStatus != null ||
      gen.subagentStreamStatus != null
    ) {
      return gi;
    }
  }
  const lastGen = msg.generations[msg.generations.length - 1];
  if (lastGen && !(typeof lastGen.content === 'string' && lastGen.content.trim())) {
    return msg.generations.length - 1;
  }
  return null;
}

/** Finds the in-flight assistant generation for a user turn (optimistic bubble before server id arrives). */
export function findInFlightAssistantForUser(
  messages: ConversationState['messages'],
  userMessageId: string
): { msgIndex: number; genIndex: number } | null {
  for (let mi = messages.length - 1; mi >= 0; mi--) {
    const msg = messages[mi];
    if (!assistantMatchesUserTurn(msg, userMessageId, messages)) continue;
    const genIndex = findInFlightGenerationInMessage(msg);
    if (genIndex !== null) {
      return { msgIndex: mi, genIndex };
    }
  }

  // When server user id arrives before optimistic id is reconciled, only one in-flight bubble is safe to reuse
  const globalInFlight: { msgIndex: number; genIndex: number }[] = [];
  for (let mi = messages.length - 1; mi >= 0; mi--) {
    const msg = messages[mi];
    if (msg.role !== MessageRole.ASSISTANT) continue;
    const genIndex = findInFlightGenerationInMessage(msg);
    if (genIndex !== null) {
      globalInFlight.push({ msgIndex: mi, genIndex });
    }
  }
  if (globalInFlight.length === 1) {
    const candidate = globalInFlight[0];
    const candidateMsg = messages[candidate.msgIndex];
    const userMsg = findUserMessageForLinkedId(messages, userMessageId);
    if (
      userMsg &&
      resolveUserMessageRecordIdForAssistant(messages, candidateMsg) === userMsg.recordId
    ) {
      return candidate;
    }
  }

  return null;
}

/** Whether an assistant stream target already exists (by server id or in-flight user turn). */
export function hasStreamingAssistantTarget(
  messages: ConversationState['messages'],
  assistantMessageId: string,
  userMessageId?: string
): boolean {
  if (
    messages.some(
      (msg) =>
        msg.recordId === assistantMessageId ||
        msg.generations.some((g) => g.id === assistantMessageId)
    )
  ) {
    return true;
  }

  return !!userMessageId && !!findInFlightAssistantForUser(messages, userMessageId);
}

/**
 * Resolves assistant message/generation for TOOL_* / SKILL_* stream events.
 * Direct chat prefers the in-flight user-turn bubble so status lines stay on one generation
 * even when a duplicate server-id placeholder exists.
 */
export function resolveStreamGenerationTarget(
  messages: ConversationState['messages'],
  assistantMessageId: string,
  streamingAssistantMessageId?: string | null,
  userMessageId?: string,
  isGroupChat = false
): { msgIndex: number; genIndex: number } | null {
  if (isGroupChat) {
    const groupPlacement = findStreamingPlacement(
      messages,
      streamingAssistantMessageId,
      assistantMessageId
    );
    if (groupPlacement) {
      return groupPlacement;
    }
  }

  if (userMessageId) {
    const inFlight = findInFlightAssistantForUser(messages, userMessageId);
    if (inFlight) {
      return inFlight;
    }
  }

  return findStreamingPlacement(
    messages,
    streamingAssistantMessageId,
    assistantMessageId,
    userMessageId
  );
}

/** Ensures an assistant bubble exists for an in-flight stream (e.g. after page refresh). */
export function ensureStreamingAssistantPlaceholder(
  messages: ConversationState['messages'],
  assistantMessageId: string,
  userMessageId: string,
  parentIds?: string[]
): ConversationState['messages'] {
  for (let mi = 0; mi < messages.length; mi++) {
    const msg = messages[mi];
    if (msg.role !== MessageRole.ASSISTANT) continue;
    let genIndex = msg.generations.findIndex((g) => g.id === assistantMessageId);
    if (genIndex === -1 && msg.recordId === assistantMessageId && msg.generations.length > 0) {
      genIndex = msg.generations.length - 1;
    }
    if (genIndex === -1) continue;
    return messages.map((m, i) => {
      if (i !== mi) return m;
      return {
        ...m,
        activeGenerationIndex: genIndex,
        generations: m.generations.map((gen, gi) =>
          gi === genIndex
            ? { ...gen, id: assistantMessageId, isThisGenerationOnGoing: true }
            : gen
        ),
      };
    });
  }

  const userMsg = findUserMessageForLinkedId(messages, userMessageId);
  const parentIndex = findAssistantParentMessageIndex(messages, parentIds);
  const existingIndex =
    parentIndex !== -1
      ? parentIndex
      : userMsg
        ? messages.findIndex(
            (m) =>
              m.role === MessageRole.ASSISTANT &&
              resolveUserMessageRecordIdForAssistant(messages, m) === userMsg.recordId
          )
        : findInFlightAssistantForUser(messages, userMessageId)?.msgIndex ?? -1;

  if (existingIndex !== -1) {
    const assistant = messages[existingIndex];
    const reuseIndex = assistant.generations.findIndex(
      (g) =>
        g.isThisGenerationOnGoing || !(typeof g.content === 'string' && g.content.trim())
    );
    return messages.map((msg, index) => {
      if (index !== existingIndex) return msg;
      if (reuseIndex !== -1) {
        return {
          ...msg,
          generations: msg.generations.map((gen, gi) =>
            gi === reuseIndex
              ? { ...gen, id: assistantMessageId, isThisGenerationOnGoing: true }
              : gen
          ),
          activeGenerationIndex: reuseIndex,
        };
      }
      return {
        ...msg,
        generations: [
          ...msg.generations,
          {
            id: assistantMessageId,
            content: '',
            createdDate: Date.now(),
            showMessageTime: formatShowMessageTimeForSend(),
            parentUserGenerationId: userMessageId || msg.userRecordId,
            isThisGenerationOnGoing: true,
          },
        ],
        activeGenerationIndex: msg.generations.length,
      };
    });
  }

  const placeholder = createStreamingAssistantPlaceholder(assistantMessageId, userMessageId);
  const userIdx = findUserMessageIndex(messages, userMessageId);
  if (userIdx === -1) return [...messages, placeholder];

  const updated = [...messages];
  updated.splice(userIdx + 1, 0, placeholder);
  return updated;
}

/** Prefer exact user recordId match before generation-id match (avoids wrong thread when ids overlap). */
export function findUserMessageForLinkedId(
  messages: Message[],
  linkedId: string
): Message | undefined {
  const byRecordId = messages.find(
    (m) => m.role === MessageRole.USER && m.recordId === linkedId
  );
  if (byRecordId) return byRecordId;

  return messages.find(
    (m) =>
      m.role === MessageRole.USER &&
      m.generations.some((g) => g.id === linkedId)
  );
}

/** Resolves all user-generation ids that belong to the same user message turn. */
export function buildUserTurnIdSet(messages: Message[], anchor: Message): Set<string> {
  const ids = new Set<string>();

  const linkId = (id?: string) => {
    if (!id) return;
    ids.add(id);
    const userMsg = findUserMessageForLinkedId(messages, id);
    if (userMsg) {
      ids.add(userMsg.recordId);
      userMsg.generations.forEach((g) => ids.add(g.id));
    }
  };

  if (anchor.role === MessageRole.USER) {
    linkId(anchor.recordId);
    anchor.generations.forEach((g) => linkId(g.id));
    return ids;
  }

  linkId(anchor.userRecordId);
  anchor.generations.forEach((g) => linkId(g.parentUserGenerationId));
  return ids;
}

export function assistantBelongsToUserTurn(message: Message, userTurnIds: Set<string>): boolean {
  if (message.userRecordId && userTurnIds.has(message.userRecordId)) return true;
  return message.generations.some(
    (g) => g.parentUserGenerationId != null && userTurnIds.has(g.parentUserGenerationId)
  );
}

/** User message recordId that owns this assistant turn (for pairing / stale-stream checks). */
export function resolveUserMessageRecordIdForAssistant(
  messages: Message[],
  assistant: Message
): string | undefined {
  if (assistant.userRecordId) {
    const userMsg = findUserMessageForLinkedId(messages, assistant.userRecordId);
    if (userMsg) return userMsg.recordId;
  }

  for (const gen of assistant.generations) {
    if (!gen.parentUserGenerationId) continue;
    const userMsg = findUserMessageForLinkedId(messages, gen.parentUserGenerationId);
    if (userMsg) return userMsg.recordId;
  }

  return undefined;
}

/** Finds assistant message whose recordId or a generation id matches parentId (regen parent). */
export function findAssistantMessageIndexByParentId(
  messages: ConversationState['messages'],
  parentId: string
): number {
  return messages.findIndex(
    (msg) =>
      (msg.role === MessageRole.ASSISTANT || msg.role === MessageRole.ASSISTANT_CHAT) &&
      (msg.recordId === parentId || msg.generations.some((g) => g.id === parentId))
  );
}

function findAssistantParentMessageIndex(
  messages: ConversationState['messages'],
  parentIds?: string[]
): number {
  if (!parentIds?.length) return -1;
  for (const parentId of parentIds) {
    const index = findAssistantMessageIndexByParentId(messages, parentId);
    if (index !== -1) return index;
  }
  return -1;
}

function generationHasVisibleContent(generation: MessageGeneration): boolean {
  const contentTrimmed = typeof generation.content === 'string' ? generation.content.trim() : '';
  const thinkTrimmed = typeof generation.thinkContent === 'string' ? generation.thinkContent.trim() : '';
  return !!(
    contentTrimmed ||
    thinkTrimmed ||
    (generation.streamBlocks && generation.streamBlocks.length > 0)
  );
}

function dropEmptyGenerationsFromAssistant(message: Message): Message {
  if (message.role !== MessageRole.ASSISTANT || message.generations.length <= 1) {
    return message;
  }

  const kept = message.generations.filter((generation, index) => {
    if (generation.isCancelled || generationHasVisibleContent(generation)) return true;
    const laterHasContent = message.generations
      .slice(index + 1)
      .some((other) => generationHasVisibleContent(other));
    return !!generation.isThisGenerationOnGoing && !laterHasContent;
  });

  if (kept.length === 0 || kept.length === message.generations.length) return message;

  const activeId = message.generations[message.activeGenerationIndex ?? 0]?.id;
  const activeGenerationIndex = Math.max(
    0,
    kept.findIndex((generation) => generation.id === activeId)
  );

  return {
    ...message,
    generations: kept,
    activeGenerationIndex,
  };
}

/** Avoid showing an empty leftover regen slot when a later filled generation exists. */
export function resolveDisplayGenerationIndex(message: Message): number {
  const active = message.activeGenerationIndex ?? 0;
  const activeGeneration = message.generations[active];
  if (
    activeGeneration &&
    (generationHasVisibleContent(activeGeneration) ||
      activeGeneration.isThisGenerationOnGoing ||
      activeGeneration.isCancelled)
  ) {
    return active;
  }

  for (let i = message.generations.length - 1; i >= 0; i -= 1) {
    const generation = message.generations[i];
    if (generationHasVisibleContent(generation) || generation.isCancelled) return i;
  }

  return Math.max(0, message.generations.length - 1);
}

/** Stop the in-flight assistant generation for a user turn (e.g. edit while streaming timeout). */
export function finalizeTurnInFlightAssistant(
  messages: ConversationState['messages'],
  userRecordId: string
): ConversationState['messages'] {
  return messages.map((message) => {
    if (message.role !== MessageRole.ASSISTANT) return message;
    if (resolveUserMessageRecordIdForAssistant(messages, message) !== userRecordId) return message;

    const hasInFlight = message.generations.some((generation) => generation.isThisGenerationOnGoing);
    if (!hasInFlight) return message;

    return {
      ...message,
      generations: message.generations.map((generation) =>
        generation.isThisGenerationOnGoing
          ? clearStreamingStatusFields({
              ...generation,
              isThisGenerationOnGoing: false,
              isThinking: false,
              isAnalyzing: false,
              isCancelled: true,
            })
          : generation
      ),
    };
  });
}

function mergeAssistantMessagePair(target: Message, source: Message): Message {
  const seenGenIds = new Set(target.generations.map((g) => g.id));
  const mergedGenerations = [
    ...target.generations,
    ...source.generations.filter((g) => !seenGenIds.has(g.id)),
  ];
  const targetActive = target.activeGenerationIndex ?? target.generations.length - 1;
  const sourceActive = source.activeGenerationIndex ?? source.generations.length - 1;
  const activeGenerationIndex = Math.min(
    Math.max(targetActive, sourceActive),
    mergedGenerations.length - 1
  );

  return {
    ...target,
    userRecordId: target.userRecordId ?? source.userRecordId,
    generations: mergedGenerations,
    activeGenerationIndex,
    toolsStatus: target.toolsStatus ?? source.toolsStatus,
  };
}

/** Merges duplicate assistant bubbles (same recordId or same user message row) into one row with generations. */
export function consolidateAssistantMessages(messages: Message[]): Message[] {
  if (messages.length < 2) return messages;

  const result: Message[] = [];
  const byRecordId = new Map<string, number>();
  const byUserMessageRecordId = new Map<string, number>();

  for (const msg of messages) {
    if (msg.role !== MessageRole.ASSISTANT) {
      result.push(msg);
      continue;
    }

    const recordKey = msg.recordId;
    const userMessageRecordId = resolveUserMessageRecordIdForAssistant(messages, msg);

    if (recordKey && byRecordId.has(recordKey)) {
      const idx = byRecordId.get(recordKey)!;
      result[idx] = mergeAssistantMessagePair(result[idx], msg);
      if (userMessageRecordId) byUserMessageRecordId.set(userMessageRecordId, idx);
      continue;
    }

    if (userMessageRecordId && byUserMessageRecordId.has(userMessageRecordId)) {
      const idx = byUserMessageRecordId.get(userMessageRecordId)!;
      const existing = result[idx];
      const sameSender =
        !existing.senderId ||
        !msg.senderId ||
        existing.senderId === msg.senderId;
      if (sameSender) {
        result[idx] = mergeAssistantMessagePair(existing, msg);
        byRecordId.set(result[idx].recordId, idx);
        continue;
      }
    }

    const idx = result.length;
    result.push(msg);
    if (recordKey) byRecordId.set(recordKey, idx);
    if (userMessageRecordId) byUserMessageRecordId.set(userMessageRecordId, idx);
  }

  return result.map(dropEmptyGenerationsFromAssistant);
}

/** Drops websocket echo rows when an edited user generation already lives on another user turn. */
export function removeEchoUserMessageRows(messages: Message[]): Message[] {
  return messages.filter((msg) => {
    if (msg.role !== MessageRole.USER) return true;

    const onlyGeneration = msg.generations.length === 1 ? msg.generations[0] : undefined;
    if (!onlyGeneration || onlyGeneration.id !== msg.recordId) return true;

    const echoedOnEditThread = messages.some(
      (other) =>
        other.recordId !== msg.recordId &&
        other.role === MessageRole.USER &&
        other.generations.some((generation) => generation.id === msg.recordId),
    );

    return !echoedOnEditThread;
  });
}

/** Parsed reconnect payload from NEW_CONNECTION_WHILE_STREAMING + SEARCH_SOURCES. */
export interface ReconnectStreamPayload {
  searchSources: SearchSource[];
  previousStreamedContent: string;
}

function normalizeSearchSourceItem(item: Record<string, unknown>): SearchSource | null {
  const pageContent = String(item.pageContent ?? item.PageContent ?? '');
  const metadata = (item.metadata ?? item.Metadata ?? {}) as Record<string, unknown>;
  const title = String(metadata.title ?? metadata.Title ?? '');
  const url = String(metadata.url ?? metadata.Url ?? '');
  if (!pageContent && !url && !title) return null;
  return {
    pageContent,
    metadata: { title, url },
  };
}

function isSearchResultReconnectItem(metadata: Record<string, unknown>): boolean {
  const url = String(metadata.url ?? metadata.Url ?? '').trim();
  const type = String(metadata.type ?? metadata.Type ?? '').toUpperCase();
  return (
    !!url ||
    type === 'SEARCH_RESULT' ||
    type === 'SEARCH_SOURCES' ||
    type === 'SEARCH'
  );
}

function isStreamTextReconnectItem(metadata: Record<string, unknown>): boolean {
  const type = String(metadata.type ?? metadata.Type ?? '').toUpperCase();
  return (
    metadata.isStreamedContent === true ||
    metadata.isPreviousStreamContent === true ||
    metadata.isStreamedMessage === true ||
    type === 'STREAMED_CONTENT' ||
    type === 'MESSAGE_CONTENT' ||
    type === 'ASSISTANT_STREAM' ||
    type === 'LLM_ANSWER' ||
    type === 'SEARCH_RESPONSE'
  );
}

/**
 * Splits NEW_CONNECTION_WHILE_STREAMING SEARCH_SOURCES data into citation sources
 * vs previously streamed assistant text (carried in pageContent entries).
 */
export function parseReconnectStreamingData(
  streamingResponse: { type?: string; data?: unknown } | null | undefined,
  messageContent?: string | null
): ReconnectStreamPayload {
  const searchSources: SearchSource[] = [];
  const textParts: string[] = [];

  if (messageContent?.trim()) {
    textParts.push(messageContent.trim());
  }

  const raw = streamingResponse?.data;
  const streamType = String(streamingResponse?.type ?? '').toUpperCase();

  // Reconnect snapshot of already-streamed answer text (SEARCH_RESPONSE / LLM_ANSWER)
  if (typeof raw === 'string' && raw.trim()) {
    if (
      streamType === 'SEARCH_RESPONSE' ||
      streamType === 'LLM_ANSWER' ||
      streamType === 'LLM_THINKING'
    ) {
      textParts.push(raw.trim());
    }
    return { searchSources, previousStreamedContent: textParts.join('') };
  }

  if (!Array.isArray(raw)) {
    return { searchSources, previousStreamedContent: textParts.join('') };
  }

  const isSearchSourcesStream = streamType === 'SEARCH_SOURCES';

  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const item = entry as Record<string, unknown>;
    const pageContent = String(item.pageContent ?? item.PageContent ?? '');
    const metadata = (item.metadata ?? item.Metadata ?? {}) as Record<string, unknown>;

    if (isStreamTextReconnectItem(metadata)) {
      if (pageContent) textParts.push(pageContent);
      continue;
    }

    if (isSearchResultReconnectItem(metadata)) {
      const source = normalizeSearchSourceItem(item);
      if (source) searchSources.push(source);
      continue;
    }

    // Non-SEARCH_SOURCES reconnect may carry assistant text in pageContent without a URL
    if (!isSearchSourcesStream && pageContent) {
      textParts.push(pageContent);
    }
  }

  return {
    searchSources,
    previousStreamedContent: textParts.join(''),
  };
}

/** Drops everything the stream itself builds, so replayed frames rebuild it in wire order. */
function resetStreamedGeneration(generation: MessageGeneration): MessageGeneration {
  return clearStreamingStatusFields({
    ...generation,
    content: '',
    thinkContent: '',
    isThinking: false,
    isAnalyzing: false,
    isAnalyzed: false,
    streamBlocks: undefined,
    streamStatusEntries: undefined,
    toolCalls: undefined,
    skillEvents: undefined,
    subagentRuns: undefined,
    a2uiSurfaces: undefined,
    codeExecutionSteps: undefined,
    isThisGenerationOnGoing: true,
    needsReplayAnswerReplace: true,
  });
}

/** One socket frame, reduced to what replay-burst tracking needs. */
export interface StreamReplayFrame {
  assistantMessageId?: string;
  userMessageId?: string;
  isReplay: boolean;
  isEndOfStream: boolean;
}

export interface StreamReplayState {
  messages: ConversationState['messages'];
  streamingAssistantMessageId?: string | null;
  isGroupChat?: boolean;
  replayingAssistantMessageIds?: string[];
}

/**
 * Replay-burst edges, tracked per assistant message id. The first reason-tagged frame of a burst
 * resets that message's streamed state so every replayed frame can then be applied like a live one;
 * an untagged frame ends the burst so a later reconnect resets again.
 * Returns null when the frame changes nothing.
 */
export function applyStreamReplayBurst(
  state: StreamReplayState,
  frame: StreamReplayFrame
): { messages: ConversationState['messages']; replayingAssistantMessageIds: string[] } | null {
  const { assistantMessageId } = frame;
  if (!assistantMessageId) return null;

  const replaying = state.replayingAssistantMessageIds ?? [];
  const wasReplaying = replaying.includes(assistantMessageId);
  const endBurst = {
    messages: state.messages,
    replayingAssistantMessageIds: replaying.filter((id) => id !== assistantMessageId),
  };

  if (frame.isEndOfStream) return wasReplaying ? endBurst : null;
  if (frame.isReplay === wasReplaying) return null;
  if (!frame.isReplay) return endBurst;

  // Prefer the server assistant id so a leftover empty in-flight gen does not
  // steal the reset from the completed bubble the UI is still showing.
  const target =
    findStreamingPlacement(
      state.messages,
      state.streamingAssistantMessageId,
      assistantMessageId
    ) ??
    resolveStreamGenerationTarget(
      state.messages,
      assistantMessageId,
      state.streamingAssistantMessageId,
      frame.userMessageId,
      state.isGroupChat
    );

  const messages = target
    ? state.messages.map((msg, msgIndex) =>
        msgIndex === target.msgIndex
          ? {
              ...msg,
              activeGenerationIndex: target.genIndex,
              generations: msg.generations.map((gen, genIndex) =>
                genIndex === target.genIndex ? resetStreamedGeneration(gen) : gen
              ),
            }
          : msg
      )
    : state.messages;

  return { messages, replayingAssistantMessageIds: [...replaying, assistantMessageId] };
}

function resolveAssistantGenerationTarget(
  messages: ConversationState['messages'],
  assistantMessageId: string,
  userMessageId?: string,
  parentIds?: string[]
): { msgIndex: number; genIndex: number } | null {
  const byId = findStreamingPlacement(messages, null, assistantMessageId, userMessageId);
  if (byId) return byId;

  const parentMsgIndex = findAssistantParentMessageIndex(messages, parentIds);
  if (parentMsgIndex !== -1) {
    const parentMsg = messages[parentMsgIndex];
    const genIndex = parentMsg.generations.findIndex((g) => g.id === assistantMessageId);
    if (genIndex !== -1) {
      return { msgIndex: parentMsgIndex, genIndex };
    }
  }

  if (userMessageId) {
    const inFlight = findInFlightAssistantForUser(messages, userMessageId);
    if (inFlight) return inFlight;
  }

  return null;
}

/**
 * NEW_CONNECTION_WHILE_STREAMING: attach stream target to the conversation and apply snapshot data.
 * Regeneration streams use parentIds (prior generation id) to add a new generation on that assistant.
 */
export function applyNewConnectionWhileStreaming(
  messages: ConversationState['messages'],
  assistantMessageId: string,
  messageContent: string | null | undefined,
  streamingResponse: { type?: string; data?: unknown } | null | undefined,
  userMessageId?: string,
  parentIds?: string[]
): ConversationState['messages'] {
  let updated = messages;
  const parentMsgIndex = findAssistantParentMessageIndex(updated, parentIds);

  if (parentMsgIndex !== -1) {
    const parentMsg = updated[parentMsgIndex];
    if (!parentMsg.generations.some((g) => g.id === assistantMessageId)) {
      const parentGenId = parentIds?.find((id) =>
        parentMsg.generations.some((g) => g.id === id)
      );
      const parentGen = parentGenId
        ? parentMsg.generations.find((g) => g.id === parentGenId)
        : undefined;

      updated = updated.map((msg, i) => {
        if (i !== parentMsgIndex) return msg;
        const newGenerations = [
          ...msg.generations,
          {
            id: assistantMessageId,
            content: '',
            createdDate: Date.now(),
            showMessageTime: formatShowMessageTimeForSend(),
            parentUserGenerationId: parentGen?.parentUserGenerationId ?? msg.userRecordId,
            ...(parentIds?.length && { parentIds }),
            isThisGenerationOnGoing: true,
          },
        ];
        return {
          ...msg,
          generations: newGenerations,
          activeGenerationIndex: newGenerations.length - 1,
        };
      });
    }
  } else {
    updated = ensureStreamingAssistantPlaceholder(
      updated,
      assistantMessageId,
      userMessageId ||
        [...updated].reverse().find((m) => isUserRoleMessage(m.role))?.recordId ||
        assistantMessageId,
      parentIds
    );
  }

  updated = updated.map((msg) => {
    if (msg.role !== MessageRole.ASSISTANT) return msg;
    const genIndex = msg.generations.findIndex((g) => g.id === assistantMessageId);
    if (genIndex === -1) return msg;
    return {
      ...msg,
      activeGenerationIndex: genIndex,
      generations: msg.generations.map((g, i) => {
        if (i !== genIndex) return g;
        const markReplace =
          g.needsReplayAnswerReplace !== false && generationHasVisibleContent(g);
        return {
          ...g,
          isThisGenerationOnGoing: true,
          ...(markReplace && { needsReplayAnswerReplace: true }),
        };
      }),
    };
  });

  return insertUserBeforeStreamingAssistant(
    hydrateAssistantMessagesOnReconnect(
      updated,
      assistantMessageId,
      messageContent,
      streamingResponse,
      userMessageId,
      parentIds
    )
  );
}

/** History fetch must not wipe an in-flight NEW_CONNECTION_WHILE_STREAMING assistant turn. */
export function preferLiveReconnectMessages(
  fetched: ConversationState['messages'],
  live: ConversationState['messages']
): ConversationState['messages'] {
  const liveSpecial = live.filter(
    (msg) =>
      msg.role === MessageRole.ASSISTANT &&
      msg.generations.some((g) => g.isThisGenerationOnGoing || g.isContentFilterViolation)
  );
  if (!liveSpecial.length) {
    if (live.length > 0 && !fetched.length) {
      return live;
    }
    return fetched;
  }
  if (!fetched.length) return insertUserBeforeStreamingAssistant(live);

  const liveIds = new Set(
    liveSpecial.flatMap((msg) => [msg.recordId, ...msg.generations.map((g) => g.id)])
  );
  const merged = fetched.map((msg) => {
    if (msg.role !== MessageRole.ASSISTANT) return msg;
    const match = liveSpecial.find(
      () => liveIds.has(msg.recordId) || msg.generations.some((g) => liveIds.has(g.id))
    );
    return match ?? msg;
  });
  for (const liveMsg of liveSpecial) {
    const alreadyPresent = merged.some(
      (msg) =>
        msg.recordId === liveMsg.recordId ||
        msg.generations.some((g) => liveMsg.generations.some((lg) => lg.id === g.id))
    );
    if (!alreadyPresent) {
      const userMsg = live.find(
        (m) =>
          isUserRoleMessage(m.role) &&
          (m.recordId === liveMsg.userRecordId ||
            m.generations.some((g) =>
              liveMsg.generations.some((lg) => lg.parentUserGenerationId === g.id)
            ))
      );
      if (userMsg && !merged.some((m) => m.recordId === userMsg.recordId)) {
        merged.push(userMsg);
      }
      merged.push(liveMsg);
    }
  }
  return insertUserBeforeStreamingAssistant(consolidateAssistantMessages(merged));
}

/** Hold reconnect frames until the user prompt is in the thread, or history has finished. */
export function shouldQueueReconnectUntilUserPrompt(
  messages: ConversationState['messages'],
  hasFetchedInitialMessages: boolean
): boolean {
  if (hasFetchedInitialMessages) return false;
  return !messages.some((msg) => isUserRoleMessage(msg.role) || msg.role === MessageRole.USER_CHAT);
}

/** If a user prompt arrived after the reconnect assistant, put that prompt back above it. */
export function insertUserBeforeStreamingAssistant(
  messages: ConversationState['messages']
): ConversationState['messages'] {
  const streamingIdx = messages.findIndex(
    (msg) =>
      msg.role === MessageRole.ASSISTANT &&
      msg.generations.some((g) => g.isThisGenerationOnGoing)
  );
  if (streamingIdx === -1) return messages;

  const prev = streamingIdx > 0 ? messages[streamingIdx - 1] : undefined;
  if (prev && (isUserRoleMessage(prev.role) || prev.role === MessageRole.USER_CHAT)) {
    return messages;
  }

  const userIdx = messages.findIndex(
    (msg, i) =>
      i > streamingIdx &&
      (isUserRoleMessage(msg.role) || msg.role === MessageRole.USER_CHAT)
  );
  if (userIdx === -1) return messages;

  const next = [...messages];
  const [user] = next.splice(userIdx, 1);
  next.splice(streamingIdx, 0, user);
  return next;
}

/**
 * Restores the reconnect citation snapshot on the target assistant generation. Replayed answer
 * text is not applied here — every replayed frame goes through the live reducer path instead.
 */
export function hydrateAssistantMessagesOnReconnect(
  messages: ConversationState['messages'],
  assistantMessageId: string,
  messageContent: string | null | undefined,
  streamingResponse: { type?: string; data?: unknown } | null | undefined,
  userMessageId?: string,
  parentIds?: string[]
): ConversationState['messages'] {
  const { searchSources } = parseReconnectStreamingData(streamingResponse, messageContent);

  if (!searchSources.length) {
    return messages;
  }

  let placement = resolveAssistantGenerationTarget(
    messages,
    assistantMessageId,
    userMessageId,
    parentIds
  );
  if (!placement && userMessageId) {
    messages = ensureStreamingAssistantPlaceholder(
      messages,
      assistantMessageId,
      userMessageId,
      parentIds
    );
    placement = resolveAssistantGenerationTarget(
      messages,
      assistantMessageId,
      userMessageId,
      parentIds
    );
  }
  if (!placement) {
    return messages;
  }

  return messages.map((msg, msgIndex) => {
    if (msgIndex !== placement.msgIndex) return msg;

    const generation = msg.generations[placement.genIndex];
    if (!generation) return msg;

    const updatedGeneration = {
      ...generation,
      searchSources,
      isSearchSourcesShow: true,
      isThisGenerationOnGoing: true,
    };

    const updatedGenerations = msg.generations.map((g, idx) =>
      idx === placement.genIndex ? updatedGeneration : g
    );

    return {
      ...msg,
      generations: updatedGenerations,
      activeGenerationIndex: placement.genIndex,
    };
  });
}

function createStreamingAssistantPlaceholder(
  targetMessageId: string,
  messageSenderId: string
): Message {
  return {
    recordId: targetMessageId,
    userRecordId: messageSenderId,
    role: MessageRole.ASSISTANT,
    activeGenerationIndex: 0,
    generations: [
      {
        id: targetMessageId,
        content: '',
        createdDate: Date.now(),
        showMessageTime: formatShowMessageTimeForSend(),
        parentUserGenerationId: messageSenderId,
        isThisGenerationOnGoing: true,
      },
    ],
  };
}

export const getUpdatedMessages = (SSEResponse: SSEResponse, currentConv: ConversationState) => {

  // console.log('SSEResponse in getUpdatedMessages---->', SSEResponse);
  if (!currentConv.messages) return [];

  const targetMessageId = SSEResponse.assistant_message_id
  const messageSenderId =
    SSEResponse.message_sender_id ??
    (SSEResponse as SSEResponse & { messageSenderId?: string }).messageSenderId;
  const parentIds =
    SSEResponse.parent_ids ??
    (SSEResponse as SSEResponse & { parentIds?: string[] }).parentIds;
  
  if (!targetMessageId) {
    return [];
  }

  const isReconnectWhileStreaming =
    SSEResponse.stream_reason === NEW_CONNECTION_WHILE_STREAMING;

  let messagesToWorkWith: typeof currentConv.messages = [...currentConv.messages];

  // Group chat: resolve the exact assistant generation (multiple assistants can share userRecordId)
  if (currentConv.isGroupChat && targetMessageId) {
    const placement = findStreamingPlacement(
      messagesToWorkWith,
      currentConv.streamingAssistantMessageId,
      targetMessageId,
      messageSenderId
    );
    if (placement) {
      messagesToWorkWith = messagesToWorkWith.map((msg, idx) =>
        idx === placement.msgIndex
          ? { ...msg, activeGenerationIndex: placement.genIndex }
          : msg
      );
    } else if (!isReconnectWhileStreaming && messageSenderId) {
      messagesToWorkWith = ensureStreamingAssistantPlaceholder(
        messagesToWorkWith,
        targetMessageId,
        messageSenderId,
        parentIds
      );
    }
  }
  
  if (!currentConv.isGroupChat && messagesToWorkWith.length > 0) {
    const lastMessage = messagesToWorkWith[messagesToWorkWith.length - 1];

    let syncedByParentBranch = false;
    if (lastMessage.role === MessageRole.ASSISTANT) {
      if (parentIds && parentIds.length > 0) {
        const parentMsgIndex = findAssistantParentMessageIndex(messagesToWorkWith, parentIds);

        if (parentMsgIndex !== -1) {
          syncedByParentBranch = true;
          const parentMsg = messagesToWorkWith[parentMsgIndex];
          const generationExists = parentMsg.generations.some((gen) => gen.id === targetMessageId);

          if (!generationExists) {
            const targetIdExistsAnywhere = messagesToWorkWith.some((msg) =>
              msg.generations.some((gen) => gen.id === targetMessageId)
            );

            if (!targetIdExistsAnywhere) {
              messagesToWorkWith = messagesToWorkWith.map((msg, index) => {
                if (index !== parentMsgIndex) return msg;

                let updatedGenerations;
                const lastGen = msg.generations[msg.generations.length - 1];
                const lastGenIsReusablePlaceholder =
                  !!lastGen &&
                  !(typeof lastGen.content === 'string' && lastGen.content.trim()) &&
                  !lastGen.streamBlocks?.length &&
                  !lastGen.a2uiSurfaces?.length;
                if (lastGen && lastGenIsReusablePlaceholder) {
                  updatedGenerations = msg.generations.map((gen, genIndex) => {
                    if (genIndex === msg.generations.length - 1) {
                      return {
                        ...gen,
                        id: targetMessageId,
                        parentIds,
                      };
                    }
                    return gen;
                  });
                } else if (msg.generations.length > 0) {
                  updatedGenerations = [
                    ...msg.generations,
                    {
                      id: targetMessageId,
                      content: '',
                      createdDate: Date.now(),
                      showMessageTime: formatShowMessageTimeForSend(),
                      parentIds,
                    },
                  ];
                } else {
                  updatedGenerations = [
                    {
                      id: targetMessageId,
                      content: '',
                      createdDate: Date.now(),
                      showMessageTime: formatShowMessageTimeForSend(),
                      parentIds,
                    },
                  ];
                }
                return {
                  ...msg,
                  generations: updatedGenerations,
                  activeGenerationIndex: updatedGenerations.length - 1,
                };
              });
            } else {
              let foundMsgIndex = -1;
              let foundGenIndex = -1;
              for (let i = 0; i < messagesToWorkWith.length; i++) {
                const genIndex = messagesToWorkWith[i].generations.findIndex(
                  (gen) => gen.id === targetMessageId
                );
                if (genIndex !== -1) {
                  foundMsgIndex = i;
                  foundGenIndex = genIndex;
                  break;
                }
              }
              if (foundMsgIndex !== -1 && foundGenIndex !== -1) {
                messagesToWorkWith = messagesToWorkWith.map((msg, index) => {
                  if (index === foundMsgIndex) {
                    return {
                      ...msg,
                      activeGenerationIndex: foundGenIndex,
                    };
                  }
                  return msg;
                });
              }
            }
          } else {
            const generationIndex = parentMsg.generations.findIndex(
              (gen) => gen.id === targetMessageId
            );
            if (generationIndex !== -1) {
              messagesToWorkWith = messagesToWorkWith.map((msg, index) => {
                if (index === parentMsgIndex) {
                  return {
                    ...msg,
                    activeGenerationIndex: generationIndex,
                  };
                }
                return msg;
              });
            }
          }
        }
      }
    }

    if (!syncedByParentBranch) {
      const placement = findStreamingPlacement(
        messagesToWorkWith,
        currentConv.streamingAssistantMessageId,
        targetMessageId,
        messageSenderId
      );
      if (placement && messagesToWorkWith[placement.msgIndex]?.role === MessageRole.ASSISTANT) {
        const targetBubble = messagesToWorkWith[placement.msgIndex];

        const needsRecordIdUpdate = targetBubble.recordId !== targetMessageId;
        const hasGenerationWithId = targetBubble.generations.some((gen) => gen.id === targetMessageId);

        const targetMsgIndex = placement.msgIndex;
        const patchGenIndex = placement.genIndex;

        if (needsRecordIdUpdate || !hasGenerationWithId) {
          const targetIdExistsAnywhere = messagesToWorkWith.some((m) =>
            m.generations.some((gen) => gen.id === targetMessageId)
          );

          messagesToWorkWith = messagesToWorkWith.map((msg, index) => {
            if (index !== targetMsgIndex) return msg;

            let updatedGenerations;
            let activeGenIndex: number | undefined;

            if (hasGenerationWithId) {
              updatedGenerations = msg.generations;
              activeGenIndex = msg.generations.findIndex((gen) => gen.id === targetMessageId);
            } else if (!targetIdExistsAnywhere) {
              if (msg.generations.length > 0) {
                updatedGenerations = msg.generations.map((gen, genIndex) => {
                  if (genIndex === patchGenIndex) {
                    return {
                      ...gen,
                      id: targetMessageId,
                    };
                  }
                  return gen;
                });
                activeGenIndex = patchGenIndex;
              } else {
                updatedGenerations = [
                  {
                    id: targetMessageId,
                    content: '',
                    createdDate: Date.now(),
                    showMessageTime: formatShowMessageTimeForSend(),
                  },
                ];
                activeGenIndex = 0;
              }
            } else {
              updatedGenerations = msg.generations;
            }

            return {
              ...msg,
              recordId: targetMessageId,
              generations: updatedGenerations,
              ...(activeGenIndex !== undefined && { activeGenerationIndex: activeGenIndex }),
            };
          });
        } else if (hasGenerationWithId) {
          const genIndex = targetBubble.generations.findIndex((gen) => gen.id === targetMessageId);
          if (genIndex !== -1) {
            messagesToWorkWith = messagesToWorkWith.map((msg, index) => {
              if (index !== targetMsgIndex) return msg;
              return {
                ...msg,
                activeGenerationIndex: genIndex,
              };
            });
          }
        }
      }
    }

  }
  
  let assistantMsgIndex = messagesToWorkWith && messagesToWorkWith.findIndex((msg) => {
    if (msg.role !== MessageRole.ASSISTANT) return false;
    let found = false;
    for (const gen of msg.generations) {
      if (gen.id == targetMessageId) {
        found = true;
        break;
      }
    }
    return found;
  });



  if (assistantMsgIndex === -1) {
    const place = findStreamingPlacement(
      messagesToWorkWith,
      currentConv.streamingAssistantMessageId,
      targetMessageId,
      messageSenderId
    );

    if (place) {
      assistantMsgIndex = place.msgIndex;
      messagesToWorkWith = messagesToWorkWith.map((msg, idx) => {
        if (idx !== place.msgIndex) return msg;
        const targetGen = msg.generations[place.genIndex];
        if (!targetGen) return { ...msg, activeGenerationIndex: place.genIndex };
        if (targetGen.id === targetMessageId) {
          return { ...msg, activeGenerationIndex: place.genIndex };
        }
        return {
          ...msg,
          activeGenerationIndex: place.genIndex,
          generations: msg.generations.map((gen, genIdx) =>
            genIdx === place.genIndex
              ? {
                  ...gen,
                  id: targetMessageId,
                  content: '',
                }
              : gen
          ),
        };
      });
    } else if (messageSenderId) {
      let messageToUpdateIndex = -1;
      let patchGenIndex = -1;

      if (!currentConv.isGroupChat) {
        const senderUserMessage = findUserMessageForLinkedId(
          messagesToWorkWith,
          messageSenderId
        );
        if (senderUserMessage) {
          messageToUpdateIndex = messagesToWorkWith.findIndex(
            (msg) =>
              msg.role === MessageRole.ASSISTANT &&
              resolveUserMessageRecordIdForAssistant(messagesToWorkWith, msg) ===
                senderUserMessage.recordId
          );
        } else {
          messageToUpdateIndex = messagesToWorkWith.findIndex(
            (msg) => msg.role === MessageRole.ASSISTANT && msg.userRecordId === messageSenderId
          );
        }
        if (
          messageToUpdateIndex !== -1 &&
          messagesToWorkWith[messageToUpdateIndex].generations.length > 0
        ) {
          const assistantMsg = messagesToWorkWith[messageToUpdateIndex];
          const inFlightIdx = findInFlightGenerationInMessage(assistantMsg);
          patchGenIndex =
            inFlightIdx ?? assistantMsg.generations.length - 1;
        }
      }

      if (messageToUpdateIndex === -1) {
        for (let mi = 0; mi < messagesToWorkWith.length; mi++) {
          const msg = messagesToWorkWith[mi];
          if (msg.role !== MessageRole.ASSISTANT) continue;
          const gi = msg.generations.findIndex(
            (g) => g.parentUserGenerationId === messageSenderId
          );
          if (gi !== -1) {
            messageToUpdateIndex = mi;
            patchGenIndex = gi;
            break;
          }
        }
      }

      if (messageToUpdateIndex !== -1 && patchGenIndex >= 0) {
        const messageToUpdate = messagesToWorkWith[messageToUpdateIndex];
        messagesToWorkWith[messageToUpdateIndex] = {
          ...messageToUpdate,
          generations: messageToUpdate.generations.map((gen, idx) => {
            if (idx !== patchGenIndex) return gen;
            return {
              ...gen,
              id: targetMessageId,
              content: '',
            };
          }),
          activeGenerationIndex: patchGenIndex,
        };
      } else {
        messagesToWorkWith = ensureStreamingAssistantPlaceholder(
          messagesToWorkWith,
          targetMessageId,
          messageSenderId,
          parentIds
        );
      }

      assistantMsgIndex = messagesToWorkWith.findIndex((msg) => {
        if (msg.role !== MessageRole.ASSISTANT) return false;
        return msg.generations.some((gen) => gen.id === targetMessageId);
      });
    }
  }

  if (assistantMsgIndex === -1) return [];

  const updatedMessages = messagesToWorkWith;
  const existingMsg = updatedMessages[assistantMsgIndex];
  const existingGenerations = existingMsg.generations;
  let updatedGenerations;

  // console.log('SSEResponse in getUpdatedMessages', SSEResponse.type);

  if (
    SSEResponse.type == SSEResponseTypes.FILE_GENERATION_ON_PROGRESS ||
    SSEResponse.type == SSEResponseTypes.IMAGE_GENERATION_SUCCESS
  ) {
    let fileData: GeneratedFile;
    // console.log('SSEResponse.data in getUpdatedMessages', SSEResponse.data)
    if (typeof SSEResponse.data !== 'object' && typeof SSEResponse.data !== 'string')
      fileData = JSON.parse(SSEResponse.data) as GeneratedFile;
    else fileData = SSEResponse.data as GeneratedFile;
    const file: Attachment = {
      fileId: fileData.file_id,
      fileName: fileData.file_name,
      extension: fileData.file_type,
      sender: MessageRole.ASSISTANT,
      status: fileData.status as FileGenerationStatus,
    };
    // Use targetMessageId instead of streamingAssistantMessageId to support concurrent streams
    const targetMessageId = SSEResponse.assistant_message_id || currentConv.streamingAssistantMessageId;
    updatedGenerations = existingGenerations.map((gen) => {
      if (gen.id === targetMessageId) {
        let found = false;
        if (SSEResponse.type == SSEResponseTypes.IMAGE_GENERATION_SUCCESS) {
          gen.generatedImages?.map((attachment) => {
            if (attachment.fileId === file.fileId) {
              attachment.status = file.status;
              found = true;
            }
          });
          if (!found) {
            (gen.generatedImages ??= []).push(file);
          }
        } else if (SSEResponse.type == SSEResponseTypes.FILE_GENERATION_ON_PROGRESS) {
          gen.attachments?.map((attachment) => {
            if (attachment.fileId === file.fileId) {
              attachment.status = file.status;
              found = true;
            }
          });
          if (!found) {
            (gen.attachments ??= []).push(file);
          }
        }
        if (fileData.message) {
          gen.content = fileData.message;
        }
        gen.HasFileGeneratedResponse = true;
        return gen;
      }
      return gen;
    });
  } else if (SSEResponse.type == SSEResponseTypes.CODE_EXECUTION_ARTIFACT) {
    // Handle artifact data structure with filename instead of file_name

    const artifactData = SSEResponse.data as any;
    const file: Attachment = {
      fileId: artifactData.file_id,
      fileName: artifactData.file_name,
      extension: artifactData.file_type,
      sender: MessageRole.ASSISTANT,
      status: FileGenerationStatus.COMPLETED, // Artifacts are typically completed when received
    };
    

    // Use targetMessageId instead of streamingAssistantMessageId to support concurrent streams
    const targetMessageId = SSEResponse.assistant_message_id || currentConv.streamingAssistantMessageId;
    updatedGenerations = existingGenerations.map((gen) => {
      if (gen.id === targetMessageId) {
        let found = false;
        gen.attachments?.map((attachment) => {
          if (attachment.fileId === file.fileId) {
            attachment.status = file.status;
            found = true;
          }
        });
        if (!found) {
          (gen.attachments ??= []).push(file);
        }
        gen.HasFileGeneratedResponse = true;
        return gen;
      }
      return gen;
    });
  } else if( 
    SSEResponse.type == SSEResponseTypes.RESPONSE_SOURCES
  ) {
    const rawSources = SSEResponse.data as SourceFile[];
    const sources: Source[] = (rawSources ?? []).map(normalizeSourceFromWire);
    // Use targetMessageId instead of streamingAssistantMessageId to support concurrent streams
    const targetMessageId = SSEResponse.assistant_message_id || currentConv.streamingAssistantMessageId;
    updatedGenerations = existingGenerations.map((gen) => {
      if (gen.id === targetMessageId) {
        return {
          ...gen,
          sources,
        };
      }
      return gen;
    });
  } else if(
    SSEResponse.type == SSEResponseTypes.SEARCH_SOURCES
  ){
    const targetMessageId = SSEResponse.assistant_message_id || currentConv.streamingAssistantMessageId;
    const reconnectPayload = isReconnectWhileStreaming
      ? parseReconnectStreamingData({ type: SSEResponse.type, data: SSEResponse.data })
      : null;
    const searchSources = reconnectPayload?.searchSources.length
      ? reconnectPayload.searchSources
      : (SSEResponse.data as SearchSource[]);

    updatedGenerations = existingGenerations.map((gen) => {
      if (gen.id === targetMessageId) {
        return {
          ...gen,
          searchSources,
          isSearchSourcesShow: true,
        };
      }
      return gen;
    });
  } else if (
    SSEResponse.type == SSEResponseTypes.IMAGE_GENERATION_STARTED 
  ) {
    const content = SSEResponse.data as string;
    // Use targetMessageId instead of streamingAssistantMessageId to support concurrent streams
    const targetMessageId = SSEResponse.assistant_message_id || currentConv.streamingAssistantMessageId;
    updatedGenerations = existingGenerations.map((gen) => {
      if (gen.id === targetMessageId) {
        return {
          ...gen,
          content: gen.content + (gen.content ? '\n' : '') + content,
          HasFileGeneratedResponse: true,
        };
      }
      return gen;
    });
  } else if(SSEResponse.type == SSEResponseTypes.IMAGE_GENERATION_FAILED) {
    const content = SSEResponse.data as string;
    // Use targetMessageId instead of streamingAssistantMessageId to support concurrent streams
    const targetMessageId = SSEResponse.assistant_message_id || currentConv.streamingAssistantMessageId;
    updatedGenerations = existingGenerations.map((gen) => {
      if (gen.id === targetMessageId) {
        return {
          ...gen,
          content: content,
          HasFileGeneratedResponse: true,
        };
      }
      return gen;
    });
  } else if (
     SSEResponse.type == SSEResponseTypes.FILE_GENERATION_STARTED ||
     SSEResponse.type == SSEResponseTypes.FILE_GENERATION_FAILED ||
     SSEResponse.type == SSEResponseTypes.FILE_GENERATION_SUCCESS
  ) {
     const content = SSEResponse.data as string;
    // Use targetMessageId instead of streamingAssistantMessageId to support concurrent streams
    const targetMessageId = SSEResponse.assistant_message_id || currentConv.streamingAssistantMessageId;
    updatedGenerations = existingGenerations.map((gen) => {
      if (gen.id === targetMessageId) {
        return {
          ...gen,
          content: content,
          HasFileGeneratedResponse: true,
          isAnalyzing: true,
          isAnalyzed: false,
        };
      }
      return gen;
    });
  } else if (SSEResponse.type == SSEResponseTypes.SERVER_ERROR) {
    // Use targetMessageId instead of streamingAssistantMessageId to support concurrent streams
    const targetMessageId = SSEResponse.assistant_message_id || currentConv.streamingAssistantMessageId;
    // The backend sends the sanitized text in `error` (data stays unset).
    const rawError =
      typeof SSEResponse.data === 'string' && SSEResponse.data.trim()
        ? SSEResponse.data
        : typeof SSEResponse.error === 'string'
          ? SSEResponse.error
          : '';
    const errorContent = rawError.trim();
    updatedGenerations = existingGenerations.map((gen) => {
      if (gen.id === targetMessageId) {
        return {
          ...gen,
          content: errorContent,
          errorCategory: SSEResponse.category,
          errorCode: SSEResponse.code,
          isAnalyzing: false,
          isAnalyzed: false,
          isThinking: false,
          thinkContent: '',
          isThisGenerationOnGoing: false,
        };
      }
      return gen;
    });
  } else if (SSEResponse.type == SSEResponseTypes.CONTEXT_WINDOW_EXCEEDED ||
    SSEResponse.type == SSEResponseTypes.IMAGE_GENERATION_LIMIT_EXCEEDED
  ) {
    // Use targetMessageId instead of streamingAssistantMessageId to support concurrent streams
    const targetMessageId = SSEResponse.assistant_message_id || currentConv.streamingAssistantMessageId;
    updatedGenerations = existingGenerations.map((gen) => {
      if (gen.id === targetMessageId) {
        return {
          ...gen,
          content: '',
          isAnalyzing: false,
          isAnalyzed: false,
          isThinking: false,
          thinkContent: '',
        };
      }
      return gen;
    });
  } else if(SSEResponse.type == SSEResponseTypes.ASSISTANT_REQUEST_PROCESSING_STARTED) {
    // No longer creating MODEL_THINKING messages - skip this event
    return updatedMessages;
  } else if(SSEResponse.type == SSEResponseTypes.LLM_THINKING) {
    const content = SSEResponse.data as string;
    // Use targetMessageId instead of streamingAssistantMessageId to support concurrent streams
    const targetMessageId = SSEResponse.assistant_message_id || currentConv.streamingAssistantMessageId;
    console.log('targetMessageId in SSEResponseTypes.LLM_THINKING', targetMessageId,"content",content);
    updatedGenerations = existingGenerations.map((gen) => {
      if (gen.id === targetMessageId) {
        return {
          ...gen,
          thinkContent: (gen.thinkContent ?? '') + content,
          isThinking: true
        };
      }
      return gen;
    });
  } else if(SSEResponse.type == SSEResponseTypes.SEARCH_RESPONSE) {
    const content = SSEResponse.data as string;
    const finishedThinking = content.includes('</think>');
    const isDynamicThinking = content.includes('<think>');

    // Use targetMessageId instead of streamingAssistantMessageId to support concurrent streams
    const targetMessageId = SSEResponse.assistant_message_id || currentConv.streamingAssistantMessageId;
    updatedGenerations = existingGenerations.map((gen) => {
      if (gen.id !== targetMessageId) {
        return gen;
      }

      let updatedGen = gen;

      if (gen.isThinking && !finishedThinking) {
        updatedGen = {
          ...gen,
          thinkContent: (gen.thinkContent ?? '') + content,
          isSearchSourcesShow: true,
        };
      } else if (gen.isThinking && finishedThinking) {
        updatedGen = {
          ...gen,
          isThinking: false,
          isSearchSourcesShow: true,
        };
      } else if (!gen.isThinking && finishedThinking) {
        const content_splits = content.split('</think>');
        updatedGen = {
          ...applyStreamAnswerText(gen, content_splits[1] ?? '', 'replace'),
          thinkContent: gen.content + content_splits[0],
          isThinking: false,
          isSearchSourcesShow: true,
        };
      } else if (!gen.isThinking && isDynamicThinking) {
        updatedGen = {
          ...gen,
          isThinking: true,
          thinkContent: content.replace('<think>', ''),
          isSearchSourcesShow: true,
        };
      } else {
        updatedGen = {
          ...applyStreamAnswerText(gen, content),
          isSearchSourcesShow: true,
        };
      }

      // Hide live tool/skill/subagent status once answer text resumes after a tool call.
      if (typeof updatedGen.content === 'string' && updatedGen.content.trim()) {
        updatedGen = clearStreamingStatusFields(updatedGen);
      }

      return updatedGen;
    });
  } else if(SSEResponse.type == SSEResponseTypes.CODE_EXECUTION_STARTED) {
    // Use targetMessageId instead of streamingAssistantMessageId to support concurrent streams
    const targetMessageId = SSEResponse.assistant_message_id || currentConv.streamingAssistantMessageId;
    updatedGenerations = existingGenerations.map((gen) => {
      if (gen.id === targetMessageId) {
        return {
          ...gen,
          isAnalyzing: true,
        };
      }
      return gen;
    });
  } else if(SSEResponse.type == SSEResponseTypes.CODE_EXECUTION_STEP) {
    const stepData = SSEResponse.data as any;
    
    // Use targetMessageId instead of streamingAssistantMessageId to support concurrent streams
    const targetMessageId = SSEResponse.assistant_message_id || currentConv.streamingAssistantMessageId;
    updatedGenerations = existingGenerations.map((gen) => {
      if (gen.id === targetMessageId) {
        return {
          ...gen,
          content: (gen.content ?? ''),
          isAnalyzing: true,  // Set analyzing to true when CODE_EXECUTION_STEP comes in
          isAnalyzed: false,  // Set analyzed to false when CODE_EXECUTION_STEP comes in
          codeExecutionSteps: [...(gen.codeExecutionSteps ?? []), stepData],
        };
      }
      return gen;
    });
   } else if(SSEResponse.type == SSEResponseTypes.RESPONSE_TIME_ANALYTICS) {
    const responseTimeData = SSEResponse.data as ResponseTimeAnalytics;
    
    // Use targetMessageId instead of streamingAssistantMessageId to support concurrent streams
    const targetMessageId = SSEResponse.assistant_message_id || currentConv.streamingAssistantMessageId;
    updatedGenerations = existingGenerations.map((gen) => {
      if (gen.id === targetMessageId) {
        return {
          ...gen,
          responseAnalytics: responseTimeData,
        };
      }
      return gen;
    });
   } else if(SSEResponse.type == SSEResponseTypes.MODEL_SELECTED) {
    const modelData = SSEResponse.data as ModelSelected;
    
    // Use targetMessageId instead of streamingAssistantMessageId to support concurrent streams
    const targetMessageId = SSEResponse.assistant_message_id || currentConv.streamingAssistantMessageId;
    updatedGenerations = existingGenerations.map((gen) => {
      if (gen.id !== targetMessageId || isSyntheticAutoModel(modelData.selected_model)) {
        return gen;
      }
      return {
        ...gen,
        usedModelId: modelData.selected_model
      };
    });
   } else if(SSEResponse.type == SSEResponseTypes.TOKEN_ANALYTICS) {
    const tokenData = SSEResponse.data as TokenAnalytics;
    const tokenModel = tokenData?.model;
    const targetMessageId = SSEResponse.assistant_message_id || currentConv.streamingAssistantMessageId;
    updatedGenerations = existingGenerations.map((gen) => {
      if (gen.id !== targetMessageId || !tokenModel || isSyntheticAutoModel(tokenModel)) {
        return gen;
      }
      return {
        ...gen,
        usedModelId: tokenModel
      };
    });
   } else if(SSEResponse.type == SSEResponseTypes.STREAM_CANCELLED) {
    // Use targetMessageId instead of streamingAssistantMessageId to support concurrent streams
    const targetMessageId = SSEResponse.assistant_message_id || currentConv.streamingAssistantMessageId;
    updatedGenerations = existingGenerations.map((gen) => {
      if (gen.id === targetMessageId) {
        return clearStreamingStatusFields({
          ...gen,
          isThinking: false,
          isThisGenerationOnGoing: false,
          isCancelled: true,
        });
      }
      return gen;
    });
   } else if (SSEResponse.type == SSEResponseTypes.CONTENT_FILTER_VIOLATION) {
    const refusal =
      typeof SSEResponse.data === 'string'
        ? SSEResponse.data
        : (SSEResponse.data as any)?.refusal ||
          (SSEResponse.data as any)?.message ||
          (SSEResponse.data as any)?.content ||
          String(SSEResponse.data ?? '');
    // Use targetMessageId instead of streamingAssistantMessageId to support concurrent streams
    const targetMessageId = SSEResponse.assistant_message_id || currentConv.streamingAssistantMessageId;
    let targetIndex = targetMessageId
      ? existingGenerations.findIndex((gen) => gen.id === targetMessageId)
      : -1;
    if (targetIndex === -1) {
      targetIndex = existingGenerations.findIndex((gen) => gen.isThisGenerationOnGoing);
    }
    if (targetIndex === -1) {
      targetIndex = Math.max(0, existingMsg.activeGenerationIndex ?? (existingGenerations.length - 1));
    }

    updatedGenerations = existingGenerations.map((gen, idx) => {
      if (idx === targetIndex) {
        let updatedGen: MessageGeneration = {
          ...gen,
          id: targetMessageId || gen.id,
          content: refusal,
          streamBlocks: [{ type: 'text', text: refusal }],
          streamStatusEntries: [],
          codeExecutionSteps: [],
          searchSources: [],
          toolCallStreamStatus: undefined,
          activeToolCallName: undefined,
          skillStreamStatus: undefined,
          activeSkillName: undefined,
          activeSkillResourceName: undefined,
          subagentStreamStatus: undefined,
          activeSubagentName: undefined,
          thinkContent: undefined,
          isThinking: false,
          isAnalyzing: false,
          isAnalyzed: false,
          isThisGenerationOnGoing: false,
          isContentFilterViolation: true,
        };

        if (isReconnectWhileStreaming) {
          updatedGen = { ...updatedGen, needsReplayAnswerReplace: false };
        }

        return updatedGen;
      }
      return gen;
    });
   } else if (SSEResponse.type == SSEResponseTypes.PLATFORM_RESOURCE_CHANGED) {
     return messagesToWorkWith;
   } else {
    const content = SSEResponse.data as string;
    // console.log('content in utils', existingGenerations)
    // No longer handling MODEL_THINKING conversion - messages are created as ASSISTANT directly
    
    // targetMessageId is already defined at the top of the function
    updatedGenerations = existingGenerations.map((gen) => {
      if (gen.id === targetMessageId) {
        // If this generation was already blocked by guardrails, discard trailing LLM_ANSWER chunks
        if (gen.isContentFilterViolation) {
          return gen;
        }
        const replaceReplayAnswer =
          isReconnectWhileStreaming && !!gen.needsReplayAnswerReplace;
        let updatedGen = applyStreamAnswerText(
          gen,
          content,
          replaceReplayAnswer ? 'replace' : 'append'
        );

        // If this is LLM_ANSWER, mark it as analyzed and not analyzing only if there were code execution steps
        if (SSEResponse.type === SSEResponseTypes.LLM_ANSWER) {
          updatedGen.isAnalyzing = false;
          // Only set isAnalyzed to true if there were CODE_EXECUTION_STEP responses
          updatedGen.isAnalyzed = !!(gen.codeExecutionSteps && gen.codeExecutionSteps.length > 0);
        }

        if (updatedGen.content?.trim()) {
          updatedGen = clearStreamingStatusFields(updatedGen);
        }

        if (isReconnectWhileStreaming) {
          updatedGen = { ...updatedGen, needsReplayAnswerReplace: false };
        }
        
        return updatedGen;
      }
      return gen;
    });
  }
  const activeGenerationIndex = updatedGenerations
    ? Math.max(
        0,
        updatedGenerations.findIndex((gen) => gen.id === targetMessageId)
      )
    : existingMsg.activeGenerationIndex;
  updatedMessages[assistantMsgIndex] = {
    ...existingMsg,
    generations: updatedGenerations ? updatedGenerations : existingGenerations,
    ...(activeGenerationIndex !== undefined && { activeGenerationIndex }),
  };
  return consolidateAssistantMessages(updatedMessages);
};

export function isValidUrl(input: string) {
  if (!input) return false;
  const match = String(input)
    .trim()
    .match(/^https?:\/\/[^\s<>"'()]+$/i);
  if (!match) return false;
  const candidate = match[0].replace(/[.,;!?]+$/, '');
  try {
    const u = new URL(candidate);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

export const placeHrefInContent = (content: string, searchSources: SearchSource[]): string => {
  /**
   * Matches both citation formats:
   * - ASCII: [1], [2], [3]  
   * - Unicode: 【1】,【2】,【3】
   * 
   * Regex breakdown:
   * (?:\[|【) - Non-capturing group: match [ OR 【
   * (\d+)     - Capture digits (citation number)
   * (?:\]|】) - Non-capturing group: match ] OR 】
   * (?!\()    - Negative lookahead: not followed by (
   */
  return content.replace(/(?:\[|【)(\d+)(?:\]|】)(?!\()/g, (match, numberStr) => {
    const index = parseInt(numberStr, 10) - 1; // markdown refs start at 1
    const source = index>=0 && index < searchSources.length ? searchSources[index] : null;
    const isValid = source && source.metadata?.url && isValidUrl(source.metadata.url);

    if (isValid) {
      return `[${numberStr}](${source.metadata.url})`;
    }

    // fallback: keep the original [number]
    // return match;
    return "";
  });
};

// ==================== Mention Utilities ====================

export const getMentionUserName = (mention: Mention): string => {
  if (!mention.user) return '';
  return `${mention.user.firstName}${mention.user.lastName ? ` ${mention.user.lastName}` : ''}`;
};

/**
 * Get the display name for a user from IamData
 */
export const getUserDisplayName = (user: IamData): string => {
  return resolveUserDisplayName(user, '');
};

/**
 * Find the position where text changed between two strings
 */
const findChangePosition = (oldText: string, newText: string): number => {
  const minLength = Math.min(oldText.length, newText.length);
  for (let i = 0; i < minLength; i++) {
    if (oldText[i] !== newText[i]) {
      return i;
    }
  }
  return minLength;
};

/**
 * Update mention positions when text changes
 * Returns updated mentions with corrected positions, removing invalid ones
 */
export const updateMentionPositions = (
  mentions: Mention[],
  oldText: string,
  newText: string
): Mention[] => {
  if (mentions.length === 0) return mentions;

  const lengthDiff = newText.length - oldText.length;
  const changeStart = lengthDiff !== 0 ? findChangePosition(oldText, newText) : 0;

  return mentions
    .map((mention) => {
      const expectedText = getMentionUserName(mention);

      // Check if mention is out of bounds
      if (mention.start >= newText.length || mention.end > newText.length) {
        return null;
      }

      // Check if mention text still matches at current position
      const currentMentionText = newText.substring(mention.start, mention.end);

      if (expectedText && currentMentionText === expectedText) {
        // Mention text matches - adjust position if change occurred before it
        if (lengthDiff !== 0 && changeStart < mention.start) {
          const newStart = mention.start + lengthDiff;
          const newEnd = mention.end + lengthDiff;

          if (newStart < 0 || newEnd > newText.length) {
            return null;
          }

          return { ...mention, start: newStart, end: newEnd };
        }

        // Change occurred within or after mention - mention might be corrupted
        if (changeStart >= mention.start && changeStart < mention.end) {
          // Try to find the mention text nearby
          const searchStart = Math.max(0, mention.start - 10);
          const foundIndex = newText.indexOf(expectedText, searchStart);

          if (foundIndex !== -1) {
            return {
              ...mention,
              start: foundIndex,
              end: foundIndex + expectedText.length,
            };
          }
          return null;
        }

        return mention;
      }

      // Mention text doesn't match - try to find it nearby
      if (expectedText) {
        const searchStart = Math.max(0, mention.start - 20);
        const searchEnd = Math.min(newText.length, mention.end + 20);
        const searchArea = newText.substring(searchStart, searchEnd);
        const foundIndex = searchArea.indexOf(expectedText);

        if (foundIndex !== -1) {
          const newStart = searchStart + foundIndex;
          const newEnd = newStart + expectedText.length;

          if (newStart >= 0 && newEnd <= newText.length) {
            return { ...mention, start: newStart, end: newEnd };
          }
        }
      }

      return null;
    })
    .filter((mention): mention is Mention => mention !== null)
    .sort((a, b) => a.start - b.start);
};

/**
 * Detect if @ trigger should open mention dropdown
 */
export interface MentionTriggerInfo {
  shouldOpen: boolean;
  startPosition: number;
  searchQuery: string;
}

export const detectMentionTrigger = (
  text: string,
  cursorPosition: number
): MentionTriggerInfo | null => {
  const textBeforeCursor = text.substring(0, cursorPosition);
  const lastAtIndex = textBeforeCursor.lastIndexOf('@');

  if (lastAtIndex === -1) {
    return null;
  }

  const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
  const textBeforeAt = textBeforeCursor.substring(0, lastAtIndex);
  const charBeforeAt = lastAtIndex > 0 ? textBeforeAt[textBeforeAt.length - 1] : '';
  const hasSpaceBeforeAt = lastAtIndex === 0 || charBeforeAt === ' ' || charBeforeAt === '\n';

  // Check if it's an email address
  const emailPattern = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const textAroundAt = textBeforeAt.slice(-50) + '@' + textAfterAt.slice(0, 50);
  const isEmail = emailPattern.test(textAroundAt);

  const hasSpaceAfterAt = textAfterAt.includes(' ');
  const hasNewlineAfterAt = textAfterAt.includes('\n');

  if (hasSpaceBeforeAt && !isEmail && !hasSpaceAfterAt && !hasNewlineAfterAt) {
    return {
      shouldOpen: true,
      startPosition: lastAtIndex,
      searchQuery: textAfterAt,
    };
  }

  return null;
};

/**
 * Adjust mention positions for markdown conversion (newlines become "  \n")
 */
export const adjustMentionsForMarkdown = (
  mentions: Mention[],
  originalMessage: string,
  cleanedMessage: string,
  leadingWhitespaceLength: number
): Mention[] => {
  if (mentions.length === 0) return mentions;

  return mentions
    .map((mention) => {
      const expectedText = getMentionUserName(mention);

      if (!expectedText) {
        console.warn('Mention has no user data:', mention);
        // Still try to adjust position
        let adjustedStart = Math.max(0, mention.start - leadingWhitespaceLength);
        let adjustedEnd = Math.min(cleanedMessage.length, mention.end - leadingWhitespaceLength);

        const newlinesBefore = (originalMessage.substring(0, mention.start).match(/\n/g) || []).length;
        const newlinesIn = (originalMessage.substring(mention.start, mention.end).match(/\n/g) || []).length;

        adjustedStart += newlinesBefore * 2;
        adjustedEnd += (newlinesBefore + newlinesIn) * 2;

        return { ...mention, start: adjustedStart, end: adjustedEnd };
      }

      // Adjust for leading whitespace removal
      let adjustedStart = mention.start - leadingWhitespaceLength;
      let adjustedEnd = mention.end - leadingWhitespaceLength;

      // Adjust for newline conversion (each \n becomes "  \n")
      const newlinesBefore = (originalMessage.substring(0, mention.start).match(/\n/g) || []).length;
      const newlinesIn = (originalMessage.substring(mention.start, mention.end).match(/\n/g) || []).length;

      adjustedStart += newlinesBefore * 2;
      adjustedEnd += (newlinesBefore + newlinesIn) * 2;

      adjustedStart = Math.max(0, adjustedStart);
      adjustedEnd = Math.min(cleanedMessage.length, adjustedEnd);

      // Verify the mention text matches
      const mentionText = cleanedMessage.substring(adjustedStart, adjustedEnd);

      if (mentionText === expectedText) {
        return { ...mention, start: adjustedStart, end: adjustedEnd };
      }

      // Try to find the mention text nearby if it doesn't match
      const searchStart = Math.max(0, adjustedStart - 20);
      const searchEnd = Math.min(cleanedMessage.length, adjustedEnd + 20);
      const searchArea = cleanedMessage.substring(searchStart, searchEnd);
      const foundIndex = searchArea.indexOf(expectedText);

      if (foundIndex !== -1) {
        const newStart = searchStart + foundIndex;
        const newEnd = newStart + expectedText.length;
        return { ...mention, start: newStart, end: newEnd };
      }

      return null;
    })
    .filter((mention): mention is Mention => mention !== null);
};


export const escapeMarkdownNumbers = (markdownContent: string) => {
  return markdownContent.replace(/^(\d+)\./gm, '$1\\.');
};

const FENCED_CODE_BLOCK_REGEX = /```[\s\S]*?```/g;

const tryExtractJsonAt = (text: string, start: number): { json: string; end: number } | null => {
  const opener = text[start];
  if (opener !== '{' && opener !== '[') return null;

  const stack = [opener === '{' ? '}' : ']'];
  let inString = false;
  let escaped = false;

  for (let i = start + 1; i < text.length; i++) {
    const ch = text[i];

    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (ch === '\\') {
        escaped = true;
      } else if (ch === '"') {
        inString = false;
      }
      continue;
    }

    if (ch === '"') {
      inString = true;
      continue;
    }

    if (ch === '{' || ch === '[') {
      stack.push(ch === '{' ? '}' : ']');
    } else if (ch === '}' || ch === ']') {
      const expected = stack.pop();
      if (ch !== expected) return null;
      if (stack.length === 0) {
        const candidate = text.slice(start, i + 1);
        try {
          JSON.parse(candidate);
          return { json: candidate, end: i + 1 };
        } catch {
          return null;
        }
      }
    }
  }

  return null;
};

const normalizeBareJsonInPlainText = (text: string): string => {
  if (!text.includes('{') && !text.includes('[')) return text;

  let result = '';
  let pos = 0;

  while (pos < text.length) {
    const remaining = text.slice(pos);
    const relativeStart = remaining.search(/(?:^|\n)[ \t]*[{[]/);
    if (relativeStart === -1) {
      result += remaining;
      break;
    }

    const candidateLineStart = pos + relativeStart;
    const lineStart = text.lastIndexOf('\n', candidateLineStart) + 1;
    const linePrefix = text.slice(lineStart, candidateLineStart);
    if (!/^[ \t]*$/.test(linePrefix)) {
      result += text[pos];
      pos += 1;
      continue;
    }

    const jsonStart = text.slice(lineStart).search(/[{[]/);
    if (jsonStart === -1) {
      result += text[pos];
      pos += 1;
      continue;
    }

    const absoluteJsonStart = lineStart + jsonStart;
    result += text.slice(pos, absoluteJsonStart);

    const extracted = tryExtractJsonAt(text, absoluteJsonStart);
    if (extracted) {
      const formatted = JSON.stringify(JSON.parse(extracted.json), null, 2);
      result += `\n\`\`\`json\n${formatted}\n\`\`\``;
      pos = extracted.end;
      continue;
    }

    result += text[absoluteJsonStart];
    pos = absoluteJsonStart + 1;
  }

  return result;
};

const normalizeBareJsonInBalancedContent = (content: string): string => {
  const fenceCount = (content.match(/```/g) || []).length;
  if (fenceCount % 2 === 1) {
    const lastFence = content.lastIndexOf('```');
    if (lastFence > -1) {
      return (
        normalizeBareJsonInBalancedContent(content.slice(0, lastFence)) + content.slice(lastFence)
      );
    }
  }

  FENCED_CODE_BLOCK_REGEX.lastIndex = 0;
  let lastIndex = 0;
  const output: string[] = [];
  let match: RegExpExecArray | null;

  while ((match = FENCED_CODE_BLOCK_REGEX.exec(content)) !== null) {
    output.push(normalizeBareJsonInPlainText(content.slice(lastIndex, match.index)));
    output.push(match[0]);
    lastIndex = match.index + match[0].length;
  }

  output.push(normalizeBareJsonInPlainText(content.slice(lastIndex)));
  return output.join('');
};

/** Wraps standalone JSON objects/arrays in markdown ```json fences for syntax highlighting. */
export const normalizeBareJsonCodeBlocks = (content: string): string => {
  if (!content || (!content.includes('{') && !content.includes('['))) return content;
  return normalizeBareJsonInBalancedContent(content);
};

/** Removes wrapping quotes from API/streamed titles (e.g. `"Introduction"` → `Introduction`). */
export const stripConversationDisplayQuotes = (name: string): string => {
  return name.trim().replace(/^["']+|["']+$/g, '').trim();
};

/** Sidebar/header label: strip quotes and fall back when title is empty or a placeholder. */
export const getConversationDisplayTitle = (
  title: string | undefined | null,
  fallback: string = DEFAULT_CONVERSATION_NAME
): string => {
  const stripped = stripConversationDisplayQuotes(title ?? '');
  if (
    !stripped ||
    stripped === DEFAULT_CONVERSATION_NAME ||
    stripped === DEFAULT_AGENT_CHAT_TITLE
  ) {
    return fallback;
  }
  return stripped;
};

/** True when a title is non-empty and not a placeholder waiting for generation. */
export const isMeaningfulConversationTitle = (title?: string | null): boolean => {
  const stripped = stripConversationDisplayQuotes(title ?? '');
  return (
    stripped.length > 0 &&
    stripped !== DEFAULT_CONVERSATION_NAME &&
    stripped !== DEFAULT_AGENT_CHAT_TITLE &&
    stripped !== DEFAULT_VOICE_CALL_TITLE
  );
};

/** Whether an individual chat should call title/generate (has a reply, still default title). */
export const shouldAutoGenerateConversationTitle = (options: {
  conversation?: Pick<
    ConversationState,
    'title' | 'isGroupChat' | 'hasManualTitle' | 'participants' | 'messages'
  > | null;
  storedTitle?: string | null;
  isIncognito?: boolean;
  hasParticipant?: boolean;
}): boolean => {
  if (options.isIncognito) return false;
  if (options.hasParticipant || options.conversation?.isGroupChat) return false;
  if (options.conversation?.hasManualTitle) return false;
  if ((options.conversation?.participants?.length ?? 0) > 1) return false;

  const title = stripConversationDisplayQuotes(
    options.storedTitle ?? options.conversation?.title ?? ''
  );
  if (isMeaningfulConversationTitle(title)) return false;

  return (
    options.conversation?.messages?.some(
      (message) =>
        message.role === MessageRole.ASSISTANT &&
        message.generations?.some((generation) => Boolean(generation?.content?.trim()))
    ) ?? false
  );
};

/** Sidebar-only gate — incognito/preview chats still send via collaboration. */
export const shouldSkipSidebarConversationEntry = (options: {
  isIncognitoChat?: boolean;
  isAgentPreviewChat?: boolean;
}): boolean => Boolean(options.isIncognitoChat || options.isAgentPreviewChat);

/** Keep incognito and agent query params on /chat/:id navigations. */
export const buildChatConversationPath = (
  conversationId: string,
  options?: { isIncognito?: boolean; agentId?: string | null },
): string => {
  const params = new URLSearchParams();
  if (options?.isIncognito) params.set('incognito-chat', 'true');
  if (options?.isIncognito && options.agentId) params.set('agentId', options.agentId);
  const qs = params.toString();
  return qs ? `/chat/${conversationId}?${qs}` : `/chat/${conversationId}`;
};

/** Fresh /chat landing while preserving incognito + optional agent selection. */
export const buildChatLandingPath = (options?: {
  isIncognito?: boolean;
  agentId?: string | null;
}): string => {
  const params = new URLSearchParams();
  if (options?.agentId) params.set('agentId', options.agentId);
  if (options?.isIncognito) params.set('incognito-chat', 'true');
  const qs = params.toString();
  return qs ? `/chat?${qs}` : '/chat';
};

/** Invitation 500 must always unlock New Chat; optional reset clears the optimistic temp thread. */
export async function runLockedNewChatInvitation<T>(
  setDisableNewChat: (disabled: boolean) => void,
  invite: () => Promise<T>,
  onInviteFailure?: () => void,
): Promise<T | undefined> {
  setDisableNewChat(true);
  try {
    return await invite();
  } catch {
    onInviteFailure?.();
    return undefined;
  } finally {
    setDisableNewChat(false);
  }
}

/** Shared resolver for sidebar + header labels (group and individual chats). */
export const resolveConversationDisplayTitle = (options: {
  storedTitle?: string | null;
  participants?: Array<Partial<IamData> & { id?: string }>;
  currentUser?: { id: string; email: string; firstName: string; lastName: string } | null;
  hasManualTitle?: boolean;
  isGroupChat?: boolean;
  fallback?: string;
}): string => {
  const stored = stripConversationDisplayQuotes(options.storedTitle ?? '');

  if (options.hasManualTitle && isMeaningfulConversationTitle(stored)) {
    return stored;
  }

  if (options.isGroupChat && options.participants && options.participants.length > 1) {
    const participantTitle = deriveGroupChatTitleFromParticipants(
      options.participants,
      options.currentUser
    );
    if (isMeaningfulConversationTitle(participantTitle)) {
      return participantTitle;
    }
  }

  if (isMeaningfulConversationTitle(stored)) {
    return stored;
  }

  return options.fallback ?? DEFAULT_CONVERSATION_NAME;
};

export const createConversationTitleFromUsersArray = (
  users: Array<Partial<IamData> & { id?: string }>
): string => {
  const names = users
    .map((user) => resolveUserDisplayName(user))
    .filter((name) => name.length > 0);

  if (names.length === 0) return DEFAULT_CONVERSATION_NAME;

  if (names.length <= 2) {
    return names.join(', ');
  }

  const firstTwoUsers = names.slice(0, 2).join(', ');
  const remainingCount = names.length - 2;
  return `${firstTwoUsers}, +${remainingCount}`;
};

/** Maps logged-in user store shape to IAM participant fields for display-name resolution. */
export const currentUserToIamParticipant = (
  user: { id: string; email: string; firstName: string; lastName: string } | null | undefined
): (Partial<IamData> & { id?: string }) | null => {
  if (!user) return null;
  return {
    itemId: user.id,
    id: user.id,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    userName: user.email,
  };
};

export const conversationHasManualTitle = (
  conversation?: { events?: Array<{ eventType: string }> } | null
): boolean =>
  !!conversation?.events?.some((event) => event.eventType === 'CONVERSATION_TITLE_UPDATED');

/** Merges store profile onto a participant row so lastName is not lost when IAM list omits it. */
export const enrichParticipantWithCurrentUser = (
  participant: Partial<IamData> & { id?: string },
  currentUser: { id: string; email: string; firstName: string; lastName: string } | null | undefined
): Partial<IamData> & { id?: string } => {
  const creator = currentUserToIamParticipant(currentUser);
  if (!creator?.itemId) return participant;
  const participantId = participant.itemId ?? participant.id;
  if (participantId !== creator.itemId) return participant;
  return { ...participant, ...creator };
};

/** Resolves who performed a group action (invite, remove, title change) for system messages. */
export const resolveGroupChatActor = (
  actorId: string | undefined,
  participants: Array<Partial<IamData> & { id?: string }>,
  options?: {
    currentUser?: { id: string; email: string; firstName: string; lastName: string } | null;
    extraParticipants?: Array<Partial<IamData> & { id?: string }>;
  }
): (Partial<IamData> & { id?: string }) | null => {
  if (!actorId) return null;

  const all = [...participants, ...(options?.extraParticipants ?? [])];
  const fromList = all.find((p) => p.itemId === actorId || p.id === actorId);
  if (fromList) {
    return options?.currentUser
      ? enrichParticipantWithCurrentUser(fromList, options.currentUser)
      : fromList;
  }

  if (options?.currentUser?.id === actorId) {
    return currentUserToIamParticipant(options.currentUser);
  }

  return null;
};

export const buildGroupConversationTitle = (
  newUsers: Array<Partial<IamData>>,
  existingParticipants: Array<Partial<IamData>>,
  currentUser: { id: string; email: string; firstName: string; lastName: string } | null | undefined
): string => {
  const creator = currentUserToIamParticipant(currentUser);
  if (existingParticipants.length > 0) {
    const users = [
      ...newUsers,
      ...existingParticipants.map((p) => enrichParticipantWithCurrentUser(p, currentUser)),
    ];
    return createConversationTitleFromUsersArray(users);
  }
  const users = creator ? [...newUsers, creator] : newUsers;
  return createConversationTitleFromUsersArray(users);
};

/** Single source for sidebar + header group chat labels from the current participant list. */
export const deriveGroupChatTitleFromParticipants = (
  participants: Array<Partial<IamData> & { id?: string }>,
  currentUser?: { id: string; email: string; firstName: string; lastName: string } | null
): string =>
  createConversationTitleFromUsersArray(
    participants.map((participant) => enrichParticipantWithCurrentUser(participant, currentUser))
  );
export interface ParsedContent {
  thinkContent: string;
  responseContent: string;
}

export function parseThinkingContent(content: string): ParsedContent {
  // Input validation
  if (!content) {
    return { thinkContent: '', responseContent: '' };
  }

  // Clean file generation tags first
  content = content
    .replace(/<llm-file-generation-response>/g, '')
    .replace(/<\/llm-file-generation-response>/g, '');

  // Find tag positions
  const openIdx = content.indexOf('<think>');
  const closeIdx = content.indexOf('</think>');

  // No tags present - just return as response
  if (openIdx === -1 && closeIdx === -1) {
    return {
      thinkContent: '',
      responseContent: content.trim()
    };
  }

  // Case 1: Complete block <think>...</think>response
  if (openIdx !== -1 && closeIdx > openIdx) {
    return {
      thinkContent: content.substring(openIdx + 7, closeIdx).trim(),
      responseContent: content.substring(closeIdx + 8).trim()
    };
  }

  // Case 2: Incomplete opening <think>...
  if (openIdx !== -1 && closeIdx === -1) {
    return {
      thinkContent: content.substring(openIdx + 7).trim(),
      responseContent: ''
    };
  }

  // Case 3: Incomplete closing ...</think>response
  if (openIdx === -1 && closeIdx !== -1) {
    return {
      thinkContent: content.substring(0, closeIdx).trim(),
      responseContent: content.substring(closeIdx + 8).trim()
    };
  }

  // Fallback (should never reach here based on our 4 cases)
  return {
    thinkContent: '',
    responseContent: content.trim()
  };
}
